import React, { useState } from "react";
import { IonPage, IonContent, IonLoading } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStudent } from "../contexts/StudentContext";
import { DEFAULT_LEARNING_PROFILE, LearningProfile, saveLearningProfile } from "../services/progressService";
import { track } from "../services/analytics";
import AuthShell from "./AuthShell";
import PageHeader from "./PageHeader";
import ProgressBar from "./ProgressBar";

/**
 * Questionnaire « Ta façon d'apprendre » (docs/PROF-IA-VIDEO.md §1) : 7 écrans, une question
 * par écran, ~1 minute. Le résultat (LearningProfile) personnalise le cours du prof IA, le chat
 * et l'explication des erreurs.
 *  - mode "onboarding" : route racine /learning-profile, juste après ProfileSetup (élève).
 *    « Plus tard » enregistre les défauts avec completedAt = 0 : on ne re-propose pas le
 *    questionnaire, il reste modifiable depuis Profil.
 *  - mode "edit" : route /home/learning-profile (Profil → « Ma façon d'apprendre »).
 */
type StepKey = "approach" | "pace" | "detail" | "helpers" | "channel" | "confidence" | "goal";
const STEPS: StepKey[] = ["approach", "pace", "detail", "helpers", "channel", "confidence", "goal"];
const SUBJECTS = ["maths", "francais", "allemand", "anglais"] as const;
type SubjectKey = (typeof SUBJECTS)[number];
type Confidence = 1 | 2 | 3;

/** Réponses en cours : les choix uniques restent vides tant que l'élève n'a pas répondu. */
interface Draft {
  approach?: LearningProfile["approach"];
  pace?: LearningProfile["pace"];
  detail?: LearningProfile["detail"];
  visual: boolean;
  analogies: boolean;
  channel?: LearningProfile["channel"];
  confidence: Partial<Record<SubjectKey, Confidence>>;
  goal?: LearningProfile["goal"];
}

/** Marqueur de session : si l'enregistrement de « Plus tard » échoue, on n'insiste pas pour autant. */
export const LEARNING_SKIP_KEY = "st_learning_skip";

function draftFrom(p: LearningProfile | undefined): Draft {
  if (!p) return { visual: true, analogies: true, confidence: {} };
  return {
    approach: p.approach,
    pace: p.pace,
    detail: p.detail,
    visual: p.visual,
    analogies: p.analogies,
    channel: p.channel,
    confidence: { ...p.confidence },
    goal: p.goal,
  };
}

function toProfile(d: Draft): LearningProfile {
  const D = DEFAULT_LEARNING_PROFILE;
  return {
    version: 1,
    approach: d.approach || D.approach,
    pace: d.pace || D.pace,
    detail: d.detail || D.detail,
    visual: d.visual,
    analogies: d.analogies,
    channel: d.channel || D.channel,
    confidence: {
      maths: d.confidence.maths || 2,
      francais: d.confidence.francais || 2,
      allemand: d.confidence.allemand || 2,
      anglais: d.confidence.anglais || 2,
    },
    goal: d.goal || D.goal,
    completedAt: Date.now(),
  };
}

const choiceClass = (selected: boolean) =>
  `w-full text-left px-4 py-3.5 rounded-xl border-2 transition flex items-center gap-3 ${
    selected ? "bg-primary-500 border-primary-400 text-white" : "bg-white/5 border-white/15 text-gray-100 hover:bg-white/10"
  }`;

interface ChoiceProps {
  selected: boolean;
  icon: string;
  label: string;
  hint?: string;
  onClick: () => void;
}
const Choice: React.FC<ChoiceProps> = ({ selected, icon, label, hint, onClick }) => (
  <button type="button" onClick={onClick} className={choiceClass(selected)}>
    <span className="text-2xl w-8 text-center flex-shrink-0">{icon}</span>
    <span className="flex-1 min-w-0">
      <span className="block font-bold">{label}</span>
      {hint && <span className={`block text-xs ${selected ? "text-white/80" : "text-gray-400"}`}>{hint}</span>}
    </span>
    {selected && <i className="fa-solid fa-circle-check text-white" />}
  </button>
);

interface QuestionnaireProps {
  initial?: LearningProfile;
  onDone: (p: LearningProfile) => Promise<void>;
  /** Présent en mode onboarding uniquement (« Plus tard ») */
  onSkip?: () => Promise<void>;
  finishLabel: string;
}

const Questionnaire: React.FC<QuestionnaireProps> = ({ initial, onDone, onSkip, finishLabel }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [d, setD] = useState<Draft>(() => draftFrom(initial));
  const key = STEPS[step];
  const total = STEPS.length;
  const last = step === total - 1;

  const answered = (() => {
    switch (key) {
      case "approach":
        return Boolean(d.approach);
      case "pace":
        return Boolean(d.pace);
      case "detail":
        return Boolean(d.detail);
      case "helpers":
        return true;
      case "channel":
        return Boolean(d.channel);
      case "confidence":
        return SUBJECTS.every((s) => Boolean(d.confidence[s]));
      case "goal":
        return Boolean(d.goal);
      default:
        return false;
    }
  })();

  const next = () => {
    if (!answered) return;
    if (last) onDone(toProfile(d));
    else setStep(step + 1);
  };

  const CONF_ICONS: Record<Confidence, string> = { 1: "😓", 2: "🙂", 3: "😎" };

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
          <span>{t("learning.step", { index: step + 1, total })}</span>
          <span>{Math.round(((step + 1) / total) * 100)} %</span>
        </div>
        <ProgressBar value={((step + 1) / total) * 100} color="linear-gradient(90deg, #3b63f6, #6d5cff)" />
      </div>

      <div key={key} className="animate-fade-in space-y-3">
        <h2 className="text-xl font-black text-white leading-snug">{t(`learning.q.${key}.title`)}</h2>
        {(key === "helpers" || key === "confidence") && <p className="text-sm text-gray-400 -mt-1">{t(`learning.q.${key}.hint`)}</p>}

        {key === "approach" && (
          <div className="space-y-2">
            <Choice selected={d.approach === "examples"} icon="🍕" label={t("learning.q.approach.examples")} hint={t("learning.q.approach.examplesHint")} onClick={() => setD({ ...d, approach: "examples" })} />
            <Choice selected={d.approach === "rule"} icon="📐" label={t("learning.q.approach.rule")} hint={t("learning.q.approach.ruleHint")} onClick={() => setD({ ...d, approach: "rule" })} />
          </div>
        )}

        {key === "pace" && (
          <div className="space-y-2">
            <Choice selected={d.pace === "slow"} icon="🐢" label={t("learning.q.pace.slow")} onClick={() => setD({ ...d, pace: "slow" })} />
            <Choice selected={d.pace === "normal"} icon="🚶" label={t("learning.q.pace.normal")} onClick={() => setD({ ...d, pace: "normal" })} />
            <Choice selected={d.pace === "fast"} icon="🚀" label={t("learning.q.pace.fast")} onClick={() => setD({ ...d, pace: "fast" })} />
          </div>
        )}

        {key === "detail" && (
          <div className="space-y-2">
            <Choice selected={d.detail === "short"} icon="⚡" label={t("learning.q.detail.short")} hint={t("learning.q.detail.shortHint")} onClick={() => setD({ ...d, detail: "short" })} />
            <Choice selected={d.detail === "detailed"} icon="🔍" label={t("learning.q.detail.detailed")} hint={t("learning.q.detail.detailedHint")} onClick={() => setD({ ...d, detail: "detailed" })} />
          </div>
        )}

        {key === "helpers" && (
          <div className="space-y-2">
            <Choice selected={d.visual} icon="📊" label={t("learning.q.helpers.visual")} onClick={() => setD({ ...d, visual: !d.visual })} />
            <Choice selected={d.analogies} icon="🏠" label={t("learning.q.helpers.analogies")} hint={t("learning.q.helpers.analogiesHint")} onClick={() => setD({ ...d, analogies: !d.analogies })} />
          </div>
        )}

        {key === "channel" && (
          <div className="space-y-2">
            <Choice selected={d.channel === "read"} icon="📖" label={t("learning.q.channel.read")} onClick={() => setD({ ...d, channel: "read" })} />
            <Choice selected={d.channel === "listen"} icon="🎧" label={t("learning.q.channel.listen")} onClick={() => setD({ ...d, channel: "listen" })} />
            <Choice selected={d.channel === "both"} icon="🎬" label={t("learning.q.channel.both")} onClick={() => setD({ ...d, channel: "both" })} />
          </div>
        )}

        {key === "confidence" && (
          <div className="space-y-2">
            {SUBJECTS.map((s) => (
              <div key={s} className="card p-3">
                <p className="text-sm font-bold text-white mb-2">{t(`learning.subjects.${s}`)}</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {([1, 2, 3] as Confidence[]).map((c) => {
                    const selected = d.confidence[s] === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setD({ ...d, confidence: { ...d.confidence, [s]: c } })}
                        className={`py-2 rounded-xl border-2 text-xs font-bold transition flex flex-col items-center gap-0.5 ${
                          selected ? "bg-primary-500 border-primary-400 text-white" : "bg-white/5 border-white/15 text-gray-200"
                        }`}>
                        <span className="text-lg leading-none">{CONF_ICONS[c]}</span>
                        <span>{t(`learning.q.confidence.${c}`)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {key === "goal" && (
          <div className="space-y-2">
            <Choice selected={d.goal === "understand"} icon="💡" label={t("learning.q.goal.understand")} onClick={() => setD({ ...d, goal: "understand" })} />
            <Choice selected={d.goal === "prepareTest"} icon="📝" label={t("learning.q.goal.prepareTest")} onClick={() => setD({ ...d, goal: "prepareTest" })} />
            <Choice selected={d.goal === "getAhead"} icon="🏃" label={t("learning.q.goal.getAhead")} onClick={() => setD({ ...d, goal: "getAhead" })} />
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-1">
        {step > 0 && (
          <button type="button" onClick={() => setStep(step - 1)} className="btn-secondary text-sm flex-shrink-0">
            <i className="fa-solid fa-arrow-left mr-1" /> {t("learning.back")}
          </button>
        )}
        <button type="button" disabled={!answered} onClick={next} className="btn-primary flex-1 text-sm">
          {last ? finishLabel : t("learning.next")} {!last && <i className="fa-solid fa-arrow-right ml-1" />}
        </button>
      </div>

      {onSkip && (
        <div className="text-center">
          <button type="button" onClick={onSkip} className="text-xs text-gray-500 py-1 underline-offset-2 hover:underline">
            {t("learning.later")}
          </button>
          <p className="text-[11px] text-gray-600">{t("learning.laterHint")}</p>
        </div>
      )}
    </div>
  );
};

const LearningProfileSetup: React.FC<{ mode: "onboarding" | "edit" }> = ({ mode }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { user, profile } = useStudent();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const leave = () => {
    if (mode === "edit") history.replace("/home/profile");
    else history.replace("/home");
  };

  const done = async (p: LearningProfile) => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      await saveLearningProfile(user.uid, p);
      track("learning_profile_completed", { skipped: false });
      // Le profil mis à jour fait démonter l'écran (guard de App) : plus de setState après.
      leave();
    } catch (e) {
      setSaving(false);
      setError(t("common.errorGeneric"));
    }
  };

  const skip = async () => {
    if (!user) return;
    try {
      sessionStorage.setItem(LEARNING_SKIP_KEY, "1");
    } catch (e) {
      /* stockage indisponible */
    }
    setSaving(true);
    try {
      // Défauts enregistrés avec completedAt = 0 : le questionnaire n'est plus re-proposé.
      await saveLearningProfile(user.uid, { ...DEFAULT_LEARNING_PROFILE, completedAt: 0 });
      track("learning_profile_completed", { skipped: true });
    } catch (e) {
      /* on n'insiste pas : le marqueur de session suffit */
    }
    // Dans tous les cas on quitte l'écran (il est démonté : pas de setState ici).
    leave();
  };

  const initial = profile?.learningProfile && profile.learningProfile.completedAt ? profile.learningProfile : undefined;

  if (mode === "edit") {
    return (
      <IonPage>
        <PageHeader title={t("learning.editTitle")} backTo="/home/profile" />
        <IonContent>
          <IonLoading isOpen={saving} message={t("common.saving")} />
          <div className="page-container px-4 py-3 space-y-4 animate-fade-in">
            <p className="text-sm text-gray-400">{t("learning.editIntro")}</p>
            {error && <p className="text-sm text-red-300">{error}</p>}
            <Questionnaire initial={initial} onDone={done} finishLabel={t("common.save")} />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <AuthShell
      title={t("learning.title")}
      subtitle={t("learning.subtitle")}
      icon="fa-wand-magic-sparkles"
      alert={{
        open: Boolean(error),
        header: t("common.information"),
        message: error || "",
        onClose: () => setError(null),
        dismissLabel: t("common.close"),
      }}>
      <IonLoading isOpen={saving} message={t("common.saving")} />
      <Questionnaire initial={initial} onDone={done} onSkip={skip} finishLabel={t("learning.finish")} />
    </AuthShell>
  );
};

export default LearningProfileSetup;
